import { type JewelSocketProfile, POISON_JEWEL_ID, toExactBigInt } from '@kenocyber/shared';
import { BPS_ONE } from './exact';
import type { DamageOrigin } from './origin';
import { damageOriginMayTrigger } from './origin';
import {
  PROC_CHANCE_DENOMINATOR,
  PROC_LEVEL_CAP,
  procChanceNumerator,
  procDamageMilli,
} from './proc-lottery';

/**
 * **O Orbe da Víbora: um STACK de veneno, sorteado por acerto direto.**
 *
 * ## O que ele é, e porque não entra na loteria ofensiva
 *
 * As três joias que já existem produzem um GOLPE. O Ciclone corta naquele
 * acerto, a Bomba substitui um projétil por outra entidade, o míssil nasce ao
 * lado da salva - e as três disputam o mesmo espaço de rolagem precisamente
 * porque duas delas ao mesmo tempo seriam um pico de dano num só quadro.
 *
 * O veneno não é um golpe. Ele **aplica estado ao inimigo**: uma marca que vive
 * seis segundos e cobra em seis parcelas, cada uma um segundo depois da
 * anterior. Não há pico a evitar, porque nada acontece no instante da aplicação
 * - o que acontece é que passa a existir uma coisa que vai acontecer.
 *
 * É por isso que ele tem **sorteio próprio** e pode coexistir com um Ciclone no
 * mesmo acerto. Metê-lo nos intervalos disjuntos da loteria ofensiva faria a
 * chance dele roubar chance às outras três, e ao contrário delas ele não compra
 * o mesmo recurso: elas competem por "o que este golpe é", ele decide "o que o
 * alvo passa a ter".
 *
 * ## O que este ficheiro NÃO faz
 *
 * Não sorteia - a rolagem vem feita, do fluxo autoritativo do participante, e
 * quem a faz é a simulação. Não conhece um alvo, um relógio, uma arena, um mob
 * nem um casco: recebe números e devolve números. Não desenha, e não sabe que
 * existe uma gota verde.
 */

/* -------------------------------------------------------------------------- */
/*  A chance                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * O teto de chance do Orbe da Víbora: **13 %**, nomeado uma vez.
 *
 * É o número que produz, através de {@link poisonChanceNumerator} e do mesmo
 * denominador derivado que as outras loterias usam, exatamente a tabela que o
 * gate V28G.M declara:
 *
 * | nível | numerador | chance |
 * | --- | --- | --- |
 * | 1 | 1 300 | 0,433333…% |
 * | 6 | 7 800 | 2,6 % |
 * | 15 | 19 500 | 6,5 % |
 * | 30 | 39 000 | **13 %**, o teto final |
 *
 * As quatro linhas saem desta única constante, sem tabela de exceções e sem
 * nenhum caso especial: `1300 x nível / 300000`. O nível 1 é a razão pela qual a
 * chance NUNCA é dividida antes de ser comparada - `13/300` é uma dízima
 * infinita (`300 = 2² x 3 x 5²`, e o três nunca desaparece), e comparar
 * `0,004333…` contra um número em vírgula flutuante seria decidir um stack pelo
 * último bit de um `double`.
 *
 * Não há linha abaixo do 30. Um nível 31 é recusado por todas as fronteiras que
 * escrevem um perfil - base, socket, API, bilhete, instantânea - e a aritmética
 * defensiva devolve-lhe o mesmo que ao 30, porque é
 * {@link procChanceNumerator} que o garante e não uma verificação escrita aqui.
 *
 * É o mesmo teto do Ciclone de Lâminas, e a coincidência é de orçamento e não de
 * cópia: as duas gastam os 30 bps por nível do V28D.2 inteiros em dano, e a
 * diferença entre elas é QUANDO o dano acontece.
 */
export const POISON_MAX_CHANCE_BPS = 1_300;

/**
 * O numerador desta joia, dado o seu nível somado.
 *
 * Inteiro, nunca dividido, e comparado contra {@link PROC_CHANCE_DENOMINATOR} -
 * o MESMO espaço de rolagem das outras loterias, porque a lei de chance é a
 * mesma lei. Acima de {@link PROC_LEVEL_CAP} devolve sempre o mesmo valor.
 */
export function poisonChanceNumerator(levelSum: number): number {
  return procChanceNumerator(POISON_MAX_CHANCE_BPS, levelSum);
}

/* -------------------------------------------------------------------------- */
/*  A duração e as parcelas                                                   */
/* -------------------------------------------------------------------------- */

/** Quantas parcelas um stack cobra. Seis, uma por segundo. */
export const POISON_TICK_COUNT = 6;

/** O intervalo entre duas parcelas do MESMO stack, em ms de simulação. */
export const POISON_TICK_INTERVAL_MS = 1_000;

/**
 * A vida inteira de um stack, em ms. **Derivada**, nunca escrita.
 *
 * Seis segundos exatos: a sexta parcela cai em `t+6` e o stack sai a seguir. Um
 * número escrito à mão aqui deixaria de estar certo no dia em que a contagem ou
 * o intervalo mudassem, e ninguém iria à procura dele.
 */
export const POISON_DURATION_MS = POISON_TICK_COUNT * POISON_TICK_INTERVAL_MS;

/* -------------------------------------------------------------------------- */
/*  A magnitude                                                               */
/* -------------------------------------------------------------------------- */

/**
 * **O dano TOTAL de um stack: 9/13 do dano-base autoritativo, em qualquer
 * nível.**
 *
 * ## Porque 9/13, e porque não é um número escolhido aqui
 *
 * Porque é o que o orçamento do V28D.2 produz com este teto de chance e **toda**
 * a parcela paga em dano:
 *
 *     dano = base x 30 x min(nível,30) x 30 x 10000
 *            -------------------------------------  = base x 9/13
 *              1300 x min(nível,30) x 10000
 *
 * Os dois lados da fração crescem juntos com o nível e cancelam-se, portanto a
 * magnitude é **exatamente 9/13 ao nível 1 e ao nível 30** - o §"o nível altera
 * somente a chance, nunca a magnitude do stack" do contrato, obtido por
 * construção em vez de por uma condição escrita à mão.
 *
 * Ao teto isso fecha a conta que o contrato declara:
 *
 *     13 % de chance x 9/13 do dano-base = 9 % de dano esperado
 *
 * que são os 900 bps de poder de combate que o nível 30 vale, os mesmos da Chama
 * da Destruição e do Pulso do Dragão.
 *
 * ## Uma divisão, e é a da loteria
 *
 * `procDamageMilli` é a função, não uma cópia dela: uma segunda implementação de
 * `9/13` seria uma segunda casa para o orçamento, e `poison.test.ts` prova a
 * igualdade contra `floor(base x 9 / 13)` nível a nível em vez de a assumir.
 *
 * `baseMilli` é o dano-base AUTORITATIVO da nave e mais nada: sem a Chama, sem
 * os Corações, sem runas, sem crítico e sem outro proc.
 */
export function poisonTotalDamageMilli(baseMilli: bigint, levelSum: bigint): bigint {
  return procDamageMilli(baseMilli, POISON_MAX_CHANCE_BPS, levelSum, BPS_ONE);
}

/**
 * **A parcela `i` de um stack, em milli.** Cumulativa, e sem perda.
 *
 *     tick(i) = floor(total x i / 6) - floor(total x (i-1) / 6)
 *
 * ## Porque a diferença de dois acumulados, e não `total / 6`
 *
 * Porque `total / 6` arredonda seis vezes e perde até cinco milésimos por
 * stack - e um stack de 30 milli entregaria 5 x 6 = 30... mas um de 31
 * entregaria 5 x 6 = 30, e o milésimo a mais desapareceria em silêncio. Sendo a
 * diferença de dois prefixos, a soma das seis parcelas **telescopa** para
 * `floor(total x 6/6) - floor(0)`, que é `total` exatamente, sejam quais forem
 * o total e o índice.
 *
 * A consequência visível é que as parcelas não são todas iguais: um total de 20
 * milli dá `3, 4, 3, 3, 4, 3`. Isso é o resto a ser pago, e não ruído.
 *
 * @param index a parcela, de 1 a {@link POISON_TICK_COUNT}.
 */
export function poisonTickDamageMilli(totalDamageMilli: bigint, index: number): bigint {
  if (!Number.isInteger(index) || index < 1 || index > POISON_TICK_COUNT) {
    throw new RangeError(`poison tick outside [1, ${POISON_TICK_COUNT}]: ${index}`);
  }
  if (totalDamageMilli <= 0n) {
    return 0n;
  }
  const count = BigInt(POISON_TICK_COUNT);
  const here = (totalDamageMilli * BigInt(index)) / count;
  const before = (totalDamageMilli * BigInt(index - 1)) / count;
  return here - before;
}

/* -------------------------------------------------------------------------- */
/*  A seleção                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * O que uma aplicação de veneno congela no instante em que nasce.
 *
 * Um campo só, e é o RESULTADO e nunca a receita - a mesma lei que congela o
 * `cutMilli` do Ciclone, o `blastMilli` da Bomba e o `damageMilli` do míssil.
 * Guardar `levelSum` obrigaria a reavaliar a fórmula seis segundos depois, e uma
 * reavaliação responde "quanto vale agora" em vez de "quanto valia quando este
 * stack nasceu". Seis segundos são tempo de sobra para comprar um ponto, trocar
 * de nave ou desencaixar a própria joia.
 */
export interface PoisonApplication {
  readonly totalDamageMilli: bigint;
}

/**
 * **A seleção.** Uma rolagem por acerto direto, e no máximo um stack.
 *
 * `roll` tem de vir de `[0, PROC_CHANCE_DENOMINATOR)` e ser produzido pelo fluxo
 * autoritativo do participante - um fluxo SÓ para isto, porque partilhá-lo com a
 * loteria ofensiva deslocaria a sequência dela.
 *
 * O intervalo é `[0, w)` e não tem vizinhos: este sorteio tem um só ocupante,
 * portanto não há ordem canónica a fixar nem cursor a avançar. O dia em que uma
 * segunda joia de estado chegar é o dia em que este ficheiro ganha um registo,
 * como `volley-lottery.ts` ganhou o seu quando a Bomba deixou de estar sozinha.
 *
 * Devolve `null` quando a rolagem cai fora, quando a nave não tem a joia, e
 * quando a origem não pode originar joia nenhuma. As três razões são diferentes
 * e nenhuma produz veneno.
 */
export function selectPoisonApplication(
  profile: JewelSocketProfile,
  origin: DamageOrigin,
  roll: number,
  baseMilli: bigint,
): PoisonApplication | null {
  /*
   * **A tabela fechada, nas suas DUAS metades** (gate V28G.P).
   *
   * A primeira diz se esta origem pode originar alguma coisa; a segunda diz se
   * ela pode originar ESTA. Uma serra pode, um tiro de canhão pode, um pet não
   * pode e um efeito de joia não pode - e nenhuma das duas simulações escreve a
   * diferença como uma condição sua.
   */
  if (!damageOriginMayTrigger(origin, 'poison_tick')) {
    return null;
  }
  if (!Number.isInteger(roll) || roll < 0 || roll >= PROC_CHANCE_DENOMINATOR) {
    throw new RangeError(`poison roll outside [0, ${PROC_CHANCE_DENOMINATOR}): ${roll}`);
  }
  if (typeof baseMilli !== 'bigint' || baseMilli < 0n) {
    throw new RangeError(`a poison base is a non-negative bigint, received ${String(baseMilli)}`);
  }
  const owned = profile.entries.find((entry) => entry.id === POISON_JEWEL_ID);
  if (!owned) {
    return null;
  }
  const levelSum = toExactBigInt(owned.levelSum, `${owned.id} levelSum`);
  const width = poisonChanceNumerator(Number(levelSum));
  if (width <= 0 || roll >= width) {
    return null;
  }
  return Object.freeze({ totalDamageMilli: poisonTotalDamageMilli(baseMilli, levelSum) });
}

/* -------------------------------------------------------------------------- */
/*  O stack, e o relógio de cada modo                                         */
/* -------------------------------------------------------------------------- */

/**
 * **O que um stack guarda, e o que ele deliberadamente NÃO guarda.**
 *
 * Os dois modos estendem isto com o vocabulário deles - o duelo endereça por
 * participante, a caça por id de entidade - e partilham exatamente estes três
 * campos porque são os únicos que a aritmética abaixo lê.
 *
 * ## `appliedAt` está no relógio do MODO, e é deliberado
 *
 * O duelo conta tiques de 50 ms e a caça conta milissegundos de 16 em 16. Um
 * campo em "segundos" obrigaria um dos dois a converter, e uma conversão com
 * resto é exatamente a maneira de duas simulações discordarem sobre em que
 * instante a terceira parcela cai. Cada modo guarda o seu número e passa o seu
 * intervalo a {@link poisonPendingTicks}.
 *
 * ## Porque `nextTickAt` e `ticksRemaining` não são campos
 *
 * Porque são **derivados**, exatamente, de `appliedAt` e de `ticksDone` - ver
 * {@link poisonNextTickAt} e {@link poisonTicksRemaining}. Guardá-los seria
 * guardar a mesma informação três vezes num documento que atravessa disco, e
 * três cópias de um facto são duas oportunidades de ele passar a discordar de si
 * mesmo. O que a persistência tem de preservar é a INFORMAÇÃO, e ela está toda
 * cá: o instante de aplicação e quantas parcelas já foram pagas dizem
 * univocamente quando cai a próxima e quantas faltam.
 */
export interface PoisonStackCore {
  /** Identidade estável, única por simulação e monotónica. Sobrevive ao disco. */
  readonly id: number;
  /** O total congelado na aplicação. Nunca relido de um perfil. */
  readonly totalDamageMilli: bigint;
  /** O instante da aplicação, no relógio do modo. */
  readonly appliedAt: number;
  /** Quantas das seis parcelas já foram cobradas. */
  ticksDone: number;
}

/**
 * Quantas parcelas deste stack estão VENCIDAS e ainda por cobrar, agora.
 *
 * A parcela `i` vence em `appliedAt + i x intervalo`, portanto o número de
 * vencidas é `floor(decorrido / intervalo)`, limitado a seis. Subtrair as já
 * pagas dá as que faltam cobrar neste instante.
 *
 * ## Porque uma comparação e não um temporizador
 *
 * Porque um temporizador teria de ser decrementado por alguém, e quem o
 * decrementa tem de correr em todos os tiques - incluindo o tique em que a sala
 * foi reconstruída de uma instantânea. Isto é uma função do relógio e do estado
 * guardado, portanto uma retoma no meio dos seis segundos continua exatamente
 * onde estava sem ninguém ter de se lembrar de nada.
 *
 * Devolve mais do que um quando um passo de relógio salta um segundo inteiro.
 * Nenhum dos dois modos o faz hoje - 50 ms e 16 ms estão muito abaixo de mil -
 * e a conta está escrita assim mesmo para que um dia que o faça pague as
 * parcelas em vez de as perder.
 */
export function poisonPendingTicks(
  stack: Pick<PoisonStackCore, 'appliedAt' | 'ticksDone'>,
  now: number,
  intervalPerTick: number,
): number {
  const elapsed = now - stack.appliedAt;
  if (elapsed < intervalPerTick) {
    return 0;
  }
  const due = Math.min(POISON_TICK_COUNT, Math.floor(elapsed / intervalPerTick));
  return Math.max(0, due - stack.ticksDone);
}

/**
 * Quando cai a próxima parcela deste stack, no relógio do modo. **Derivado.**
 *
 * Sem significado depois da sexta - `poisonIsSpent` responde a essa pergunta - e
 * escrito na mesma para que "o próximo tick" seja uma coisa que se pergunta, em
 * vez de uma coisa que cada chamador recalcula à sua maneira.
 */
export function poisonNextTickAt(
  stack: Pick<PoisonStackCore, 'appliedAt' | 'ticksDone'>,
  intervalPerTick: number,
): number {
  return stack.appliedAt + (stack.ticksDone + 1) * intervalPerTick;
}

/** Quantas parcelas faltam a este stack. **Derivado**, e nunca guardado. */
export function poisonTicksRemaining(stack: Pick<PoisonStackCore, 'ticksDone'>): number {
  return Math.max(0, POISON_TICK_COUNT - stack.ticksDone);
}

/** Se este stack já pagou as seis. Um stack gasto sai da lista no mesmo tique. */
export function poisonIsSpent(stack: Pick<PoisonStackCore, 'ticksDone'>): boolean {
  return stack.ticksDone >= POISON_TICK_COUNT;
}

/* -------------------------------------------------------------------------- */
/*  Os fluxos                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Os rótulos dos fluxos de VENENO, versionados como todos os outros.
 *
 * ## Porque um fluxo próprio, por participante e por finalidade
 *
 * Porque o contrato o exige, e a razão é a de sempre: consumir a rolagem do
 * veneno de um fluxo partilhado deslocaria tudo o que viesse depois - runas,
 * spawns, temporizador do boss, pets, a rolagem por salva, a loteria ofensiva -
 * e uma corrida com a mesma semente deixaria de ser a mesma corrida por causa
 * de uma joia.
 *
 * ## E porque ele é consumido CONDICIONALMENTE
 *
 * Porque serve uma única finalidade e mais ninguém o lê, que é a mesma razão
 * pela qual o fluxo de alvos do míssil também o é. A regra de consumir sempre
 * existe para fluxos cuja sequência OUTRA coisa observa; aqui a única coisa que
 * observa a sequência é o próximo acerto direto do mesmo atacante, e a
 * instantânea guarda o estado do gerador.
 */
export const PVP_POISON_RNG_LABEL = 'pvp-jewel-poison-v1';
export const HUNT_POISON_RNG_LABEL = 'hunt-jewel-poison-v1';
/**
 * O gémeo do MUNDO ABERTO, com a mesma finalidade e a mesma unidade.
 *
 * Um rótulo próprio, e não uma reutilização: um rótulo é o que separa duas
 * sequências, e "de que modo é este fluxo" tem de continuar a ser legível numa
 * linha de import. A cadência e a razão de este fluxo ser separado dos outros
 * são as mesmas de sempre.
 */
export const OPEN_WORLD_POISON_RNG_LABEL = 'open-world-jewel-poison-v1';
